import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HELP_TOPICS } from "@/lib/helpContent";
import { X, HelpCircle, ChevronRight, Sparkles } from "lucide-react";

// HelpModal — full-screen sheet with sidebar nav (desktop) + horizontal tabs
// (mobile). Renders Phase-10A help content from helpContent.js. Opens via the
// "?" button in the Topbar. ESC closes.
//
// PROPS:
//   open: bool — controlled visibility
//   onClose: () => void — invoked when user dismisses
//   initialTopic: string — id of the first topic to show (defaults to first)
export default function HelpModal({ open, onClose, initialTopic }) {
  const [activeId, setActiveId] = useState(initialTopic || HELP_TOPICS[0].id);

  // Reset to initialTopic each time the modal is opened (so users always see
  // the topic they asked for, never the leftover from the last session).
  useEffect(() => {
    if (open) {
      setActiveId(initialTopic || HELP_TOPICS[0].id);
    }
  }, [open, initialTopic]);

  // ESC dismisses + scroll-lock the body while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const active = useMemo(
    () => HELP_TOPICS.find((t) => t.id === activeId) || HELP_TOPICS[0],
    [activeId],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4"
      data-testid="help-modal-overlay"
      onClick={(e) => {
        // Click on backdrop closes; click inside the panel doesn't.
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="relative flex flex-col w-full h-full sm:max-w-5xl sm:h-[min(90vh,720px)] sm:rounded-2xl border border-[#2a2a30] bg-[#0a0a0b] shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-modal-title"
        data-testid="help-modal-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-4 border-b border-[#2a2a30] bg-[#0d0d0e]">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#ff6b35]/10 text-[#ff6b35] shrink-0">
              <HelpCircle size={18} />
            </span>
            <div className="min-w-0">
              <h2
                id="help-modal-title"
                className="text-base sm:text-lg font-semibold text-white truncate"
              >
                Bedienungsanleitung
              </h2>
              <p className="text-xs text-[#9a9aa3] hidden sm:block">
                Wähle links ein Thema oder navigiere mit den Tabs.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors"
            aria-label="Schließen"
            data-testid="help-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col sm:flex-row flex-1 min-h-0">
          {/* Mobile: horizontal scrollable tabs */}
          <nav
            className="sm:hidden flex gap-1.5 overflow-x-auto px-4 py-3 border-b border-[#2a2a30] bg-[#0d0d0e]"
            data-testid="help-modal-nav-mobile"
          >
            {HELP_TOPICS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveId(t.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  activeId === t.id
                    ? "bg-[#ff6b35] text-white"
                    : "bg-[#17171a] text-[#9a9aa3] hover:text-white"
                }`}
                data-testid={`help-modal-tab-mobile-${t.id}`}
              >
                {t.title}
                {t.badge && (
                  <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-300">
                    {t.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Desktop sidebar */}
          <nav
            className="hidden sm:flex flex-col w-64 shrink-0 border-r border-[#2a2a30] bg-[#0d0d0e] overflow-y-auto py-4"
            data-testid="help-modal-nav-desktop"
          >
            {HELP_TOPICS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveId(t.id)}
                className={`group flex items-center justify-between gap-2 mx-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                  activeId === t.id
                    ? "bg-[#1f1f23] text-white"
                    : "text-[#9a9aa3] hover:text-white hover:bg-[#17171a]"
                }`}
                data-testid={`help-modal-tab-${t.id}`}
              >
                <span className="truncate flex items-center gap-2">
                  {t.title}
                  {t.badge && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                      <Sparkles size={9} />
                      {t.badge}
                    </span>
                  )}
                </span>
                <ChevronRight
                  size={14}
                  className={`transition-transform ${
                    activeId === t.id
                      ? "text-[#ff6b35] translate-x-0"
                      : "text-[#3a3a40] -translate-x-1 group-hover:translate-x-0"
                  }`}
                />
              </button>
            ))}
          </nav>

          {/* Content panel */}
          <article
            className="flex-1 overflow-y-auto px-5 sm:px-8 py-6"
            data-testid={`help-modal-content-${active.id}`}
            key={active.id}
          >
            <header className="flex items-center gap-2 mb-5">
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                {active.title}
              </h3>
              {active.badge && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                  <Sparkles size={10} />
                  {active.badge}
                </span>
              )}
            </header>

            {active.comingSoon ? (
              <div
                className="rounded-xl border border-[#2a2a30] bg-[#17171a]/60 p-6 text-center"
                data-testid="help-modal-coming-soon"
              >
                <div className="text-3xl mb-2" aria-hidden="true">🔜</div>
                <p className="text-sm text-[#9a9aa3] leading-relaxed">
                  Diese Funktion wird gerade gebaut und ist bald verfügbar.
                </p>
              </div>
            ) : (
              <div className="space-y-4 text-[15px] text-[#d4d4d8] leading-relaxed">
                {(active.body || []).map((block, idx) => (
                  <HelpBlock key={idx} block={block} onCloseModal={onClose} />
                ))}
              </div>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}

function HelpBlock({ block, onCloseModal }) {
  if (block.kind === "p") {
    return <p className="text-[#d4d4d8]">{block.text}</p>;
  }
  if (block.kind === "tip") {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm">
        <span className="font-semibold text-emerald-300">Tipp:</span>{" "}
        <span className="text-emerald-50/90">{block.text}</span>
      </div>
    );
  }
  if (block.kind === "list") {
    return (
      <ul className="space-y-3">
        {block.items.map((it, i) => (
          <li
            key={i}
            className="rounded-lg border border-[#2a2a30] bg-[#0d0d0e] px-4 py-3"
          >
            <div className="font-semibold text-white">{it.label}</div>
            <div className="text-sm text-[#9a9aa3] mt-1">{it.text}</div>
          </li>
        ))}
      </ul>
    );
  }
  if (block.kind === "qa") {
    return (
      <div className="rounded-lg border border-[#2a2a30] bg-[#0d0d0e] px-4 py-3">
        <div className="font-semibold text-white">{block.q}</div>
        <div className="text-sm text-[#9a9aa3] mt-1.5 leading-relaxed">
          {block.a}
        </div>
      </div>
    );
  }
  if (block.kind === "actions") {
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        {block.items.map((a, i) => (
          <Link
            key={i}
            to={a.href}
            onClick={onCloseModal}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#1f1f23] hover:bg-[#2a2a30] text-white text-sm font-medium border border-[#2a2a30] hover:border-[#3a3a40] transition-colors"
            data-testid={`help-modal-action-${i}`}
          >
            {a.label}
          </Link>
        ))}
      </div>
    );
  }
  return null;
}
